import { FC } from "react";
import {
  Box,
  Stack,
  useTheme,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import {
  DateRangePicker,
  FormikCheckbox,
  FormikSelectField,
  FormikSelectSearchField
} from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { FormLabelText, LabelRowBox } from "./InnerForm.styled";

interface Values {
  reportMode: string;
  object: string[];
  objectIds: string[];
  assignedToAllObjects: boolean;
  accountId: string;
  driverId: string;
  keyId: string;
}

interface Props {
  objects: Array<any>;
  dataLoading?: boolean;
  translator: any;
  fetchOnDebounce: (value: string) => void;
  fetchAccountOnDebounce: (value: string) => void;
  onChange?: () => void;
  values: Values;
  setFieldValue: any;
  handleDateChange: (value: any) => void;
  dateValues: any;
  dateRangeLoading: boolean;
  searchedMinimalAccounts: Array<any>;
  searchedMinimalAccountsLoading: boolean;
  users: any[];
  accountKeys: any[];
  accountKeysLoading: boolean;
}

const InnerForm: FC<Props> = ({
  objects,
  dataLoading = false,
  translator,
  fetchOnDebounce,
  fetchAccountOnDebounce,
  onChange,
  values,
  setFieldValue,
  handleDateChange,
  dateValues,
  dateRangeLoading,
  searchedMinimalAccounts,
  searchedMinimalAccountsLoading,
  users,
  accountKeys,
  accountKeysLoading
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);

  // Safety check to prevent rendering when values is undefined
  if (!values) {
    return null;
  }

  const handleReportModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: string
  ) => {
    if (newMode !== null) {
      setFieldValue("reportMode", newMode);
      if (newMode === "byDriver") {
        setFieldValue("objectIds", []);
        setFieldValue("assignedToAllObjects", false);
      } else {
        setFieldValue("driverId", null);
      }
    }
  };

  return (
    <Stack spacing={2}>
      {/* Toggle Button for Report Mode */}
      <Box>
        <ToggleButtonGroup
          value={values.reportMode}
          exclusive
          onChange={handleReportModeChange}
          aria-label="report mode"
          sx={{
            width: "100%",
            backgroundColor: "#F3F4F6",
            borderRadius: "8px",
            padding: "4px",
            border: "none",
            "& .MuiToggleButtonGroup-grouped": {
              border: "none",
              "&:not(:first-of-type)": {
                borderRadius: "8px",
                marginLeft: "0px"
              },
              "&:first-of-type": {
                borderRadius: "8px"
              }
            },
            "& .MuiToggleButton-root": {
              flex: 1,
              textTransform: "none",
              borderRadius: "8px",
              border: "none",
              color: "#374151",
              fontWeight: 500,
              padding: "8px 16px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&.Mui-selected": {
                backgroundColor: "white",
                color: "#111827",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                "&:hover": {
                  backgroundColor: "white"
                }
              },
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.5)"
              }
            }
          }}
        >
          <ToggleButton value="byDriver" aria-label="by driver">
            {t("titles.byDriver")}
          </ToggleButton>
          <ToggleButton value="byObject" aria-label="by object">
            {t("titles.byObject")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Date Range - Always visible */}
      <FormLabelText>{t("reports.selectDateRange")}</FormLabelText>
      <Box
        sx={{
          marginTop: "2px !important"
        }}
      >
        <DateRangePicker
          name="datepicker"
          onChange={handleDateChange}
          value={dateValues}
          disabled={dateRangeLoading}
          theme={theme}
          translator={t}
          fullWidth
          disableFutureDates
        />
      </Box>

      {/* By Driver Mode */}
      {values.reportMode === "byDriver" && (
        <>
          <FormikSelectField
            key={`driverId-${values.keyId || "empty"}`}
            theme={theme}
            label={translator("driver.label", {
              ns: "formFieldsTranslation"
            })}
            placeholder={translator("driver.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="driverId"
            options={users ?? []}
            optionLabelKey="email"
            optionValueKey="uniqueId"
            searchable
            required
            onChange={(e: any) => {
              if (e?.target?.value && values.keyId) {
                setFieldValue("keyId", "");
              }
              onChange?.();
            }}
            fullWidth
          />
        </>
      )}

      {/* By Object Mode */}
      {values.reportMode === "byObject" && (
        <>
          <FormikCheckbox
            theme={theme}
            name="assignedToAllObjects"
            label={translator("selectAllObjects.label", {
              ns: "formFieldsTranslation"
            })}
          />

          <LabelRowBox>
            <FormLabelText>
              {translator("objects.label", { ns: "formFieldsTranslation" })}
            </FormLabelText>
          </LabelRowBox>
          <FormikSelectSearchField
            theme={theme}
            fetchOnDebounce={fetchOnDebounce}
            placeholder={translator("objects.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="objectIds"
            options={objects}
            optionLabelKey="name"
            optionValueKey="uniqueId"
            loading={dataLoading}
            multiple
            searchable
            onChange={onChange}
            fullWidth
            disabled={values.assignedToAllObjects}
            sx={{
              marginTop: "0 !important",
              "& .MuiAutocomplete-hasClearIcon.css-1x6bjyf-MuiAutocomplete-root .MuiOutlinedInput-root":
                {
                  paddingRight: "9px"
                }
            }}
            //@ts-ignore
            showStates
            showDropdownIcon
          />
        </>
      )}
    </Stack>
  );
};

export default InnerForm;
